export const API_BASE_URL =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) ||
"https://srinivasafleet.com/api";
function getAuthToken(getToken) {
  return typeof getToken === "function" ? getToken() : localStorage.getItem("token");
}

function buildQuery(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, v);
  });
  return qs.toString();
}
async function apiGet(apiBaseUrl, path, params, getToken) {
  const token = getAuthToken(getToken);
  const base = apiBaseUrl || API_BASE_URL;
  const url = `${base}${path}?${buildQuery(params)}`;

  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const text = await res.text();
    throw new Error(
      `Expected JSON from ${url} but got "${contentType || "unknown"}". ` +
        `Check that apiBaseUrl points at your FastAPI server, not the frontend. ` +
        `Response started with: ${text.slice(0, 60)}`
    );
  }

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch {
    }
    throw new Error(`${res.status} ${detail}`);
  }

  return res.json();
}

export function fetchAuditLogs(apiBaseUrl, getToken, filters = {}) {
  return apiGet(
    apiBaseUrl,
    "/audit/logs",
    {
      table_name: filters.table_name || undefined,
      record_id: filters.record_id || undefined,
      user_id: filters.user_id || undefined,
      action: filters.action || undefined,
      date_from: toIsoOrUndefined(filters.date_from),
      date_to: toIsoOrUndefined(filters.date_to),
      limit: filters.limit,
    },
    getToken
  );
}

export function fetchReportDownloads(apiBaseUrl, getToken, filters = {}) {
  return apiGet(
    apiBaseUrl,
    "/audit/reports/downloads",
    {
      report_type: filters.report_type || undefined,
      user_id: filters.user_id || undefined,
      date_from: toIsoOrUndefined(filters.date_from),
      date_to: toIsoOrUndefined(filters.date_to),
      limit: filters.limit,
    },
    getToken
  );
}

export function fetchDownloadSummary(apiBaseUrl, getToken, filters = {}) {
  return apiGet(
    apiBaseUrl,
    "/audit/reports/downloads/summary",
    {
      date_from: toIsoOrUndefined(filters.date_from),
      date_to: toIsoOrUndefined(filters.date_to),
    },
    getToken
  );
}

export function toIsoOrUndefined(localDatetimeValue) {
  if (!localDatetimeValue) return undefined;
  const d = new Date(localDatetimeValue);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

export function fmtTimestamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ACTION_LABELS = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
};

export function actionLabel(action) {
  const key = String(action || "").toLowerCase();
  return ACTION_LABELS[key] || action || "Unknown";
}

/* ─────────────────────────────────────────────────────────────
   Fields that change on nearly every save but carry no meaning
   for a human reviewing the audit trail. These are stripped out
   before grouping so "Updated" events don't show up for saves
   that didn't actually change anything the client cares about.
───────────────────────────────────────────────────────────────*/
const IGNORED_FIELDS = new Set([
  "updated_at",
  "updated_by",
  "created_at",
  "password_hash",
]);

/* Friendly labels for the table (record type) an event belongs to. */
const TABLE_LABELS = {
  users: "Staff",
  vehicles: "Vehicle",
  suppliers: "Supplier",
  drivers: "Driver",
  messages: "Message",
  vehicle_assignments: "Vehicle Assignment",
  master_branch: "Branch",
  master_service_location: "Service Location",
};

export function tableLabel(tableName) {
  const key = String(tableName || "").toLowerCase();
  return TABLE_LABELS[key] || (tableName || "Record");
}

/* Friendly labels for individual changed fields. */
const FIELD_LABELS = {
  full_name: "Full Name",
  email: "Email",
  mobile: "Mobile",
  role_id: "Role",
  designation: "Designation",
  service_location_id: "Service Location",
  branch_id: "Branch",
  address: "Address",
  city: "City",
  pincode: "Pincode",
  status: "Status",
  is_active: "Active",
  is_blocked: "Blocked",
  photo_url: "Photo",
  financial_year_id: "Financial Year",
};

export function fieldLabel(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return String(key || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* Short "field: old → new" text for the collapsed card, so the
   client can see what changed without expanding every row. */
export function summarizeFields(fields, max = 2) {
  if (!fields || fields.length === 0) return "";
  const parts = fields.slice(0, max).map((f) => fieldLabel(f.key));
  const extra = fields.length - parts.length;
  return extra > 0 ? `${parts.join(", ")} +${extra} more` : parts.join(", ");
}

export function groupLogRows(rows) {
  const byId = new Map();
  for (const row of rows) {
    if (!byId.has(row.id)) {
      byId.set(row.id, {
        id: row.id,
        user_id: row.user_id,
        staff_name: row.staff_name,
        staff_code: row.staff_code,
        action: row.action,
        table_name: row.table_name,
        record_id: row.record_id,
        record_name: row.record_name,
        changed_at: row.changed_at,
        fields: [],
      });
    }
    if (row.changed_field && !IGNORED_FIELDS.has(row.changed_field)) {
      byId.get(row.id).fields.push({
        key: row.changed_field,
        old: row.old_value,
        next: row.new_value,
      });
    }
  }

  const events = Array.from(byId.values()).filter((ev) => {
    // Create/delete events are meaningful on their own even with
    // no field-level diff. Update events with zero remaining
    // fields only touched ignored columns (e.g. just updated_at)
    // and add nothing a client needs to see — drop them.
    const action = String(ev.action || "").toLowerCase();
    if (action === "update") return ev.fields.length > 0;
    return true;
  });

  return events.sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));
}